import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { clamp, sum } from 'lodash';
import gql from 'graphql-tag';

import { AUTOCOMPLETE_RESULTS_COUNT, SEGMENT_PAGE_CONTEXTS } from '../constants';

import { DokkioTippy } from '../ui';

import useWindowWidth from '../hooks/useWindowWidth';

import { track } from '../services/Analytics';

import { DataContext } from './Data';
import SearchAutocompleteResults, { autocompleteMainDisplayObjectFragment } from './SearchAutocompleteResults';

const HEIGHT_ITEM_PX = 25;
const HEIGHT_BETWEEN_GROUPS_PX = 10;
const MOBILE_HEIGHT_BETWEEN_GROUPS_PX = 24;
const HEIGHT_OFFSET_PX = 150;
const MOBILE_HEIGHT_OFFSET_PX = 190;
const BLUR_DELAY_MS = 250; // Delay so that clicks in autocomplete panel actually work

const QUERY_MAIN_DISPLAY_OBJECTS_GROUPS = gql`
  query mainDisplayObjectsAutocompleteGroups(
    $count: Int
    $name: String
  ) {
    mainDisplayObjectsAutocompleteGroups(
      count: $count
      name: $name
    ) {
      dokkioClass
      groupType
      total
      mainDisplayObjects {
        ...autocompleteMainDisplayObjectFragment
      }
    }
  }
  ${autocompleteMainDisplayObjectFragment}
`;

const getHeightForData = (data, isMobile) => {
  const heightBetweenGroups = isMobile ? MOBILE_HEIGHT_BETWEEN_GROUPS_PX : HEIGHT_BETWEEN_GROUPS_PX;
  let height = data.length * heightBetweenGroups;
  for (const group of data) {
    height += group.mainDisplayObjects.length * HEIGHT_ITEM_PX;
  }
  return height;
};

const getDataLimitPerGroup = (data, rowsPerGroup) => data.map((group) => {
  const newGroup = Object.assign({}, group);
  if (group.mainDisplayObjects.length > rowsPerGroup) {
    newGroup.mainDisplayObjects = newGroup.mainDisplayObjects.slice(0, rowsPerGroup);
  }
  return newGroup;
});

const adjustDataForHeight = (data, isMobile) => {
  const heightOffset = isMobile ? MOBILE_HEIGHT_OFFSET_PX : HEIGHT_OFFSET_PX;
  const targetMaxHeight = window.innerHeight - heightOffset;
  if (getHeightForData(data, isMobile) < targetMaxHeight) {
    return data;
  }
  for (let i = AUTOCOMPLETE_RESULTS_COUNT - 1; i >= 2; i -= 1) {
    const newData = getDataLimitPerGroup(data, i);
    if (getHeightForData(newData, isMobile) < targetMaxHeight) {
      return newData;
    }
  }
  return getDataLimitPerGroup(data, 1);
};

export const Search = ({
  autocompletePlacement,
  segmentPageCtxt,
}) => {
  const { account } = useContext(DataContext);
  const { push } = useHistory();
  const { query } = useApolloClient();
  const { isMobile } = useWindowWidth();
  const [hasFocus, setHasFocus] = useState(false);
  const [q, setQ] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);
  const [autocompleteData, setAutocompleteData] = useState([]);
  const linkRefs = useRef([]);
  const inputRef = useRef(null);

  const loadAutocomplete = async () => {
    if (q.length < 2) {
      return;
    }

    setLoadingAutocomplete(true);
    const { data } = await query({
      // speculative fix against other places fetching the same objects but needing missing fields
      fetchPolicy: 'no-cache',
      query: QUERY_MAIN_DISPLAY_OBJECTS_GROUPS,
      variables: {
        count: AUTOCOMPLETE_RESULTS_COUNT,
        name: q,
      },
    });
    setLoadingAutocomplete(false);
    if (q === inputRef.current.value) {
      setAutocompleteData(data.mainDisplayObjectsAutocompleteGroups);
    }
  };

  useEffect(() => {
    loadAutocomplete();
  }, [q]);

  const dataForDisplay = useMemo(
    () => adjustDataForHeight(autocompleteData, isMobile),
    [autocompleteData, window.innerHeight, isMobile],
  );

  const adjustSelectedIndex = (indexChange) => {
    setSelectedIndex((oldIndex) => {
      const newIndex = oldIndex === null ? 0 : oldIndex + indexChange;
      const minIndex = 0;
      const maxIndex = (
        sum(dataForDisplay.map(({ mainDisplayObjects }) => mainDisplayObjects.length)) - 1
      );
      return clamp(newIndex, minIndex, maxIndex);
    });
  };

  const reset = () => {
    inputRef.current.blur();
    setQ('');
    setSelectedIndex(null);
  };

  const onFullTextSearch = () => {
    track('Search Keyword', {
      SearchClick: 'SearchBox',
      SearchText: q,
      PageCtxt: segmentPageCtxt,
    });
    reset();
    push({
      pathname: `/a/${account.id}/search`,
      search: `?search=${encodeURIComponent(JSON.stringify({ q }))}`,
    });
  };

  const onChange = (e) => {
    setQ(e.target.value);
    setSelectedIndex(null);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      inputRef.current.blur();
      setSelectedIndex(null);
    } else if (e.key === 'Enter') {
      if (linkRefs.current[selectedIndex]
        && linkRefs.current[selectedIndex].click) {
        linkRefs.current[selectedIndex].click();
        reset();
      } else {
        onFullTextSearch();
      }
    } else if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      adjustSelectedIndex(e.key === 'ArrowDown' ? 1 : -1);
    }
  };

  return (
    <DokkioTippy
      visible={hasFocus && q.length >= 2}
      placement={autocompletePlacement}
    >
      <DokkioTippy.Trigger>
        <div className="component-Search flex flex-grow relative border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="mr-3 h-4 w-4 text-gray-400 dark:text-gray-600" />
          </div>
          <input
            className="block h-full desktop:h-auto w-full pl-9 py-2 pr-3 rounded-lg bg-white dark:bg-black text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-600"
            name="q"
            value={q}
            autoComplete="off"
            placeholder="Global Search"
            ref={inputRef}
            onFocus={() => {
              setHasFocus(true);
              track('Search BoxClicked', { PageCtxt: segmentPageCtxt });
              loadAutocomplete();
            }}
            onBlur={() => setTimeout(() => setHasFocus(false), BLUR_DELAY_MS)}
            onChange={onChange}
            onKeyDown={onKeyDown}
          />
        </div>
      </DokkioTippy.Trigger>
      <DokkioTippy.Panel>
        <SearchAutocompleteResults
          dataForDisplay={dataForDisplay}
          loading={loadingAutocomplete}
          onLinkMouseEnter={index => () => setSelectedIndex(index)}
          onLinkNavigation={reset}
          q={q}
          segmentPageCtxt={segmentPageCtxt}
          selectedIndex={selectedIndex}
          setLinkRef={({ ref, index }) => { linkRefs.current[index] = ref; }}
        />
      </DokkioTippy.Panel>
    </DokkioTippy>
  );
};

Search.propTypes = {
  autocompletePlacement: PropTypes.oneOf(['right', 'bottom']).isRequired,
  segmentPageCtxt: PropTypes.oneOf(SEGMENT_PAGE_CONTEXTS).isRequired,
};

export default Search;
